import React, { useState, useEffect } from "react";
import axios from "axios";
import BASE_URL from "../../config/config";

const PaymentGet = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [downloadingId, setDownloadingId] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("token");
  };

  // Fetch payments from API
  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = getToken();

      if (!token) {
        setError("No authentication token found. Please login first.");
        setLoading(false);
        return;
      }

      console.log("Fetching payments with token:", token);

      const response = await axios.get(`${BASE_URL}/payments/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("API Response:", response.data);

      // Handle different response structures
      let paymentsData = [];
      if (response.data && Array.isArray(response.data)) {
        paymentsData = response.data;
      } else if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        paymentsData = response.data.data;
      } else if (
        response.data &&
        response.data.payments &&
        Array.isArray(response.data.payments)
      ) {
        paymentsData = response.data.payments;
      } else if (response.data && typeof response.data === "object") {
        // If it's a single object, wrap it in an array
        paymentsData = [response.data];
      }

      setPayments([...paymentsData].reverse());
      setFilteredPayments([...paymentsData].reverse());
      setError(null);
    } catch (err) {
      console.error("Error fetching payments:", err);
      if (err.response) {
        // Server responded with error status
        if (err.response.status === 401) {
          setError(
            "Authentication failed. Token may be expired. Please login again.",
          );
        } else if (err.response.status === 403) {
          setError("You do not have permission to access these payments.");
        } else if (err.response.status === 404) {
          setError("Payments endpoint not found. Please check the API URL.");
        } else {
          setError(
            `Server error: ${err.response.data?.message || err.response.statusText}`,
          );
        }
      } else if (err.request) {
        setError(
          "Network error. Please check if the backend server is running on http://localhost:8080",
        );
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Update the downloadReceipt function to use txnid
  const downloadReceipt = async (txnid) => {
    try {
      setDownloadingId(txnid);
      const token = getToken();

      if (!token) {
        setError("No authentication token found. Please login first.");
        return;
      }

      const response = await axios.get(
        `https://deetyasoft.com/api/payments/download-receipt/${txnid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob", // Important for file download
        },
      );

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `receipt_${txnid}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading receipt:", err);
      if (err.response) {
        if (err.response.status === 401) {
          setError("Authentication failed. Please login again.");
        } else if (err.response.status === 404) {
          setError("Receipt not found for this transaction.");
        } else {
          setError(
            `Download failed: ${err.response.data?.message || err.response.statusText}`,
          );
        }
      } else if (err.request) {
        setError("Network error. Please check your connection.");
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...payments];

    // Search filter (by name, email, phone, productinfo)
    if (searchTerm) {
      filtered = filtered.filter(
        (payment) =>
          (payment.firstname &&
            payment.firstname
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (payment.email &&
            payment.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (payment.phone && payment.phone.includes(searchTerm)) ||
          (payment.productinfo &&
            payment.productinfo
              .toLowerCase()
              .includes(searchTerm.toLowerCase())),
      );
    }

    // Status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((payment) => {
        const status = getPaymentField(
          payment,
          ["status", "payment_status"],
          "completed",
        ).toLowerCase();
        return status === filterStatus.toLowerCase();
      });
    }

    // Date range filter
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter((payment) => {
        const paymentDate = new Date(
          payment.created_at || payment.date || payment.payment_date,
        );
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        return paymentDate >= startDate && paymentDate <= endDate;
      });
    } else if (dateRange.start) {
      filtered = filtered.filter((payment) => {
        const paymentDate = new Date(
          payment.created_at || payment.date || payment.payment_date,
        );
        const startDate = new Date(dateRange.start);
        return paymentDate >= startDate;
      });
    } else if (dateRange.end) {
      filtered = filtered.filter((payment) => {
        const paymentDate = new Date(
          payment.created_at || payment.date || payment.payment_date,
        );
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        return paymentDate <= endDate;
      });
    }

    setFilteredPayments(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, filterStatus, dateRange, payments]);

  useEffect(() => {
    fetchPayments();
  }, []);

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPayments.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format currency
  const formatAmount = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return "₹0.00";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(numAmount);
  };

  // Helper function to safely get payment fields
  const getPaymentField = (payment, fieldNames, defaultValue = "N/A") => {
    for (let field of fieldNames) {
      if (payment[field] && payment[field] !== "") {
        return payment[field];
      }
    }
    return defaultValue;
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setDateRange({ start: "", end: "" });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading payments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto mt-5 p-2">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-2 rounded">
          <p className="font-bold">Error</p>
          <p>{error}</p>
          <button
            onClick={fetchPayments}
            className="mt-3 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-400 to-blue-600 px-6 py-4">
          <h1 className="text-2xl font-bold text-white">Payments History</h1>
          <p className="text-blue-100 mt-1">
            Total Payments: {filteredPayments.length}
          </p>
        </div>

        {/* Filter Section */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search Input */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Search
              </label>
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="success">Success</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Date Range - Start */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Date
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) =>
                  setDateRange({ ...dateRange, start: e.target.value })
                }
                className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date Range - End */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To Date
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) =>
                  setDateRange({ ...dateRange, end: e.target.value })
                }
                className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filter Actions */}
          {(searchTerm ||
            filterStatus !== "all" ||
            dateRange.start ||
            dateRange.end) && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-500">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Mobile Number
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Productinfo
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Payment Recieve Url
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Company Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length > 0 ? (
                currentItems.map((payment, index) => (
                  <tr
                    key={payment.id || payment._id || index}
                    className="hover:bg-gray-50 transition duration-150"
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      {indexOfFirstItem + index + 1}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getPaymentField(payment, ["firstname"], "N/A")}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getPaymentField(payment, ["phone"], "N/A")}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getPaymentField(payment, ["email"], "N/A")}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-sm font-semibold text-green-600">
                        {formatAmount(getPaymentField(payment, ["amount"], 0))}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium text-green-600">
                        {getPaymentField(payment, ["productinfo"], "NA")}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium text-green-600">
                        {getPaymentField(payment, ["udf1"], "NA")}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium text-green-600">
                        {getPaymentField(payment, ["udf2"], "NA")}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${
                          getPaymentField(
                            payment,
                            ["status", "payment_status"],
                            "completed",
                          ).toLowerCase() === "completed" ||
                          getPaymentField(
                            payment,
                            ["status", "payment_status"],
                            "completed",
                          ).toLowerCase() === "success"
                            ? "bg-green-100 text-green-800"
                            : getPaymentField(
                                  payment,
                                  ["status", "payment_status"],
                                  "completed",
                                ).toLowerCase() === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : getPaymentField(
                                    payment,
                                    ["status", "payment_status"],
                                    "completed",
                                  ).toLowerCase() === "failed"
                                ? "bg-red-100 text-red-800"
                                : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {getPaymentField(
                          payment,
                          ["status", "payment_status"],
                          "Completed",
                        )}
                      </span>
                    </td>

                    <td className="px-4 py-2 whitespace-nowrap">
                      <button
                        onClick={() => downloadReceipt(payment.txnid)} // Changed from payment.id to payment.txnid
                        disabled={downloadingId === payment.txnid}
                        className={`inline-flex items-center px-2 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-white 
    ${
      downloadingId === payment.txnid
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-blue-600 hover:bg-blue-700"
    } 
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150`}
                      >
                        {downloadingId === payment.txnid ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                      
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                              />
                            </svg>
                          
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="10"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="mt-2">No payments found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                  <span className="font-medium">
                    {Math.min(indexOfLastItem, filteredPayments.length)}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium">{filteredPayments.length}</span>{" "}
                  results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                      currentPage === 1
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                      currentPage === totalPages
                        ? "text-gray-300 cursor-not-allowed"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentGet;
